<template>
  <b-modal
    v-model="localModel"
    header-close-content=""
    id="from-external-source-modal"
    body-class="modal-body"
    modal-class="from-external-source-modal"
    :title="$t('GenerateFromExternalData')"
    centered
    @hide="onHide"
    @shown="reset"
    @close="hideDialog"
    no-close-on-backdrop
    no-close-on-esc>
    <intro v-if="vm.currentStep === 'intro'" />

    <existing-catalog
      v-if="vm.currentStep === 'existingCatalog'"
      v-model:selectedExistingCatalogId="vm.selectedExistingCatalogId"
      @setCatalogName="vm.selectedExistingCatalogName = $event" />

    <catalog-options
      v-if="vm.currentStep === 'catalogOptions'"
      @stepChange="setExternalDataSelection"
      @selectedExternalData="setExternalDataSelection"
      :fields="vm.fields"
      :groupedCommands="vm.groupedCommands" />

    <catalog-naming v-if="vm.currentStep === 'catalogNaming'" @stepChange="setCatalogName" @change="onCatalogNameChange" />

    <catalog-finishing
      v-if="vm.currentStep === 'catalogFinishing'"
      v-model:openGeneratedCatalogue="vm.openGeneratedCatalogue"
      :generatedCatalogData="vm.generatedCatalogData"
      :errorPassed="vm.error"
      @finish="onFinish" />

    <template #modal-footer>
      <b-btn v-if="hasBackButton" class="mr-2" :disabled="backButtonDisabled" @click="onBack">
        {{ backButtonText }}
      </b-btn>

      <b-btn v-if="hasNextButton" :disabled="nextButtonDisabled" @click="onNext">
        {{ nextButtonText }}
      </b-btn>
    </template>
  </b-modal>
</template>

<script setup lang="ts">
import { useChannelCreatorActions, useChannelCreatorMutations } from "@ChannelCreator/composables/useChannelCreator";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { useGlobalParameters, useLicenses, useUserSettings } from "@common/composable/useRootStore";
import { useCatalogNodeMutations } from "@ContentEditor/store/composable/useCatalogNode";
import activityApi from "@services/activity";
import apps from "@services/applications";
import automationServerApi from "@services/automationServer";
import catalogApi from "@services/catalogs.js";
import { computed, reactive } from "vue";
import catalogFinishing from "./FromExternalSource/CatalogFinisherExternal.vue";
import catalogNaming from "./FromExternalSource/CatalogNamingExternal.vue";
import catalogOptions from "./FromExternalSource/CatalogOptionsExternal.vue";
import existingCatalog from "./FromExternalSource/ExistingCatalogExternal.vue";
import intro from "./FromExternalSource/IntroExternal.vue";

//#region store
const { saveUserSettings } = useUserSettings();

const { loadCatalogs, updateTargetCatalog } = useChannelCreatorActions();
const { clearDirtyStatus: clearChannelCreatorDirtyStatus } = useChannelCreatorMutations();

const { "clear-dirty-status": clearCatalogNodeDirtyStatus } = useCatalogNodeMutations();

const props = defineProps<{
  value: boolean;
}>();

const emits = defineEmits<{
  (e: "input", value: boolean): void;
}>();

//#endregion

const { checkForLicensed } = useLicenses();
const { primaryLanguageCode } = useContentLanguages();
const { globalParameters } = useGlobalParameters();

const localModel = computed({
  get() {
    return props.value;
  },
  set(nv: boolean) {
    emits("input", nv);
  }
});

type Step = "intro" | "existingCatalog" | "catalogOptions" | "catalogNaming" | "catalogFinishing";

type ViewModel = {
  currentStep: Step;
  connectionState: boolean | null;
  selectedExistingCatalogId: string | null;
  selectedExistingCatalogName: string | null;

  commandId: string | null;
  file: File | null;
  fileData: any | null;
  fields: { artNrTypeSelected: number; artNrSourceSelected: number | null } | null;

  selectedArticleNumberType: any | null;
  newCatalogName: string | null;
  catalogNameIsValid: boolean;
  finalData: any | null;
  generatedCatalogData: any | null;
  fetchedSourceFields: any | null;
  groupedCommands: any | null;
  singleColumnName: string | null;

  openGeneratedCatalogue: boolean;
  error: any | null;
};

const getInitialViewModel = (): ViewModel => ({
  currentStep: "intro",
  connectionState: null,
  selectedExistingCatalogId: null,
  selectedExistingCatalogName: null,
  commandId: null,
  file: null,
  fileData: null,
  fields: null,
  selectedArticleNumberType: null,
  newCatalogName: null,
  catalogNameIsValid: false,
  finalData: null,
  generatedCatalogData: null,
  fetchedSourceFields: null,
  groupedCommands: null,
  singleColumnName: null,
  openGeneratedCatalogue: false,
  error: null
});

const vm = reactive<ViewModel>(getInitialViewModel());

const buttonConfig: Record<
  Step,
  {
    back?: { text: string; action: () => void; disabled: () => boolean };
    next: { text: string; action: () => void; disabled: () => boolean };
  }
> = {
  intro: {
    back: undefined,
    next: {
      text: "Continue",
      action: () => stepChange({ currentState: "intro" as Step, nextState: "existingCatalog" }),
      disabled: () => false
    }
  },
  existingCatalog: {
    back: {
      text: "Back",
      action: () => stepChange({ currentState: "existingCatalog", nextState: "intro" }),
      disabled: () => false
    },
    next: {
      text: "Continue",
      action: () => stepChange({ currentState: "existingCatalog", nextState: "catalogOptions" }),
      disabled: () => vm.selectedExistingCatalogId === null
    }
  },
  catalogOptions: {
    back: {
      text: "Back",
      action: () => stepChange({ currentState: "catalogOptions", nextState: "existingCatalog" }),
      disabled: () => false
    },
    next: {
      text: "Continue",
      action: () => stepChange({ currentState: "catalogOptions", nextState: "catalogNaming" }),
      disabled: () => vm.singleColumnName === null || vm.selectedArticleNumberType === null || vm.connectionState !== true
    }
  },
  catalogNaming: {
    back: {
      text: "Back",
      action: () => stepChange({ currentState: "catalogNaming", nextState: "catalogOptions" }),
      disabled: () => false
    },
    next: {
      text: "Continue",
      action: () => setCatalogName({ currentState: "catalogNaming", nextState: "catalogFinishing" }),
      disabled: () => !vm.newCatalogName || !vm.catalogNameIsValid
    }
  },
  catalogFinishing: {
    back: undefined,
    next: {
      text: "Close",
      action: () => onFinish(),
      disabled: () => false
    }
  }
};

const hasBackButton = computed(() => !!buttonConfig[vm.currentStep].back);
const backButtonDisabled = computed(() => buttonConfig[vm.currentStep].back?.disabled() ?? false);
const backButtonText = computed(() => buttonConfig[vm.currentStep].back?.text ?? "Back");

const hasNextButton = computed(() => !!buttonConfig[vm.currentStep].next);
const nextButtonDisabled = computed(() => buttonConfig[vm.currentStep].next.disabled());
const nextButtonText = computed(() => buttonConfig[vm.currentStep].next.text);

const onBack = () => buttonConfig[vm.currentStep].back?.action();
const onNext = () => buttonConfig[vm.currentStep].next.action();

function stepChange(e: { currentState: Step; nextState: Step }) {
  vm.currentStep = e.nextState;
  if (vm.currentStep === "intro") {
    reset();
  }
}

function onHide() {
  vm.currentStep = "intro";
  vm.selectedExistingCatalogId = null;
  vm.generatedCatalogData = null;
}

const hideDialog = () => {
  localModel.value = false;
};

function reset() {
  Object.assign(vm, getInitialViewModel());
}

function setExternalDataSelection(e: {
  singleColumnName: string | null;
  selectedArticleNumberType: any | null;
  connectionState: boolean | null;
  commandId: string | null;
}) {
  vm.singleColumnName = e.singleColumnName;
  vm.selectedArticleNumberType = e.selectedArticleNumberType;
  vm.connectionState = e.connectionState;
  vm.commandId = e.commandId;
}

async function setCatalogName(e: { currentState: Step; nextState: Step }) {
  stepChange(e);
  if (vm.currentStep === "catalogFinishing") {
    await generateCatalog();
  }
}

function onCatalogNameChange(e: { name: string; isValid: boolean }) {
  vm.newCatalogName = e.name;
  vm.catalogNameIsValid = e.isValid;
}

async function generateCatalog() {
  const data = {
    commandId: vm.commandId,
    selectedArticleNumberType: vm.selectedArticleNumberType,
    newCatalogName: vm.newCatalogName,
    selectedCatalogId: vm.selectedExistingCatalogId
  };

  try {
    vm.generatedCatalogData = await catalogApi.generateFromExternalData(primaryLanguageCode.value, data);
    await createActivity();

    if (checkForLicensed("AutomationServer")) {
      await executeAutomation();
    }
  } catch (exception: any) {
    vm.error = exception;
  }
}

async function createActivity() {
  try {
    if (globalParameters.value.IsActivityFeedEnabled) {
      const activityCriterias = [
        {
          severity: "Information",
          applicationId: apps.Modules.ChannelCreator.Id,
          process: "Create",
          objectType: "Catalog",
          objectValue: vm.newCatalogName || "",
          status: "Successful",
          action: "Completed",
          descriptionKey: "CatalogGeneratedFromCSV",
          descriptionValues: [vm.newCatalogName]
        }
      ];

      await activityApi.createActivity(activityCriterias);
    }
  } catch (err) {
    console.error("Activity creation failed:", err);
  }
}

async function executeAutomation() {
  try {
    await automationServerApi.executeAutomationsIfRequired(["CatalogCreated"]);
  } catch (err) {
    console.error("Automation execution failed:", err);
  }
}

function onFinish() {
  if (vm.openGeneratedCatalogue && vm.generatedCatalogData?.catalogId) {
    const catalogId = vm.generatedCatalogData.catalogId;
    try {
      loadCatalogs();
      //Timeout prevents JStree bug from loading it twice.
      setTimeout(() => {
        updateTargetCatalog(catalogId);
        saveUserSettings({ ChannelCreatorTargetCatalogId: catalogId });
        clearChannelCreatorDirtyStatus();
        clearCatalogNodeDirtyStatus();
      }, 100);
    } catch (err: unknown) {
      console.error("Reload catalogs failed:", err);
    }
  }
  hideDialog();
  vm.error = null;
}
</script>
